import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useCredits } from './CreditContext';
import api from './api'; // Import your API utility

const VideoGenerationContext = createContext();

// Create a default initial state
const initialState = {
    selectedInterests: [],
    videoCards: [],
    analyzing: false,
    sourceVideo: null,
    selectedModel: localStorage.getItem('preferredVideoModel') || 'runway' // Load from localStorage with fallback
};

export const VideoGenerationProvider = ({ children }) => {
  // Initialize state with default values
  const [selectedInterests, setSelectedInterests] = useState(initialState.selectedInterests);
  const [videoCards, setVideoCards] = useState(initialState.videoCards);
  const [analyzing, setAnalyzing] = useState(initialState.analyzing);
  const [sourceVideo, setSourceVideo] = useState(initialState.sourceVideo);
  const [selectedModel, setSelectedModel] = useState(initialState.selectedModel);
  // const { updateCreditsAfterGeneration } = useCredits();

  const isUserAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!token; // Convert to boolean
  };

  // Add state for credits
  const [credits, setCredits] = useState({
    total: 0,
    used: 0,
    remaining: 0
  });

  // Function to fetch updated credit information
  // const fetchCredits = async () => {
  //   // Only fetch credits if the user is authenticated
  //   if (!isUserAuthenticated()) {
  //     console.log('User not authenticated, skipping credit fetch in VideoGenerationContext');
  //     return;
  //   }

  //   try {
  //     const response = await api.getUserCredits();
  //     setCredits({
  //       total: response.data.totalCredits,
  //       used: response.data.creditsUsed,
  //       remaining: response.data.totalCredits - response.data.creditsUsed
  //     });
  //   } catch (error) {
  //     console.error('Error fetching credits:', error);
  //   }
  // };

  // Call this in useEffect to load initial credits only if authenticated
  // useEffect(() => {
  //   if (isUserAuthenticated()) {
  //     fetchCredits();
  //   }
  // }, []);

  // Create a ref for the success audio
  const successAudioRef = useRef(null);
  
  // Initialize the audio element
  useEffect(() => {
    successAudioRef.current = new Audio('/success-sound.mp3');
    successAudioRef.current.volume = 0.7; // Set volume to 70%
  }, []);

  // Function to play success sound
  const playSuccessSound = () => {
    if (successAudioRef.current) {
      // Reset the audio to the beginning if it's already playing
      successAudioRef.current.currentTime = 0;
      successAudioRef.current.play().catch(e => console.error("Error playing audio:", e));
    }
  };
  
  // Load state from localStorage on initial mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('videoGenerationState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        setSelectedInterests(parsedState.selectedInterests || []);
        setVideoCards(parsedState.videoCards || []);
        setAnalyzing(parsedState.analyzing || false);
        setSelectedModel(parsedState.selectedModel || 'runway');
        
        // Resume polling for any in-progress generations only if authenticated
        if (isUserAuthenticated() && parsedState.videoCards) {
          parsedState.videoCards.forEach((card, index) => {
            if (card.isGenerating && card.taskId) {
              pollTaskStatus(card.taskId, index);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading saved state:', error);
    }
  }, []);
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      const stateToSave = {
        selectedInterests,
        videoCards,
        analyzing,
        selectedModel
      };
      localStorage.setItem('videoGenerationState', JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Error saving state:', error);
    }
  }, [selectedInterests, videoCards, analyzing, selectedModel]);
  
  // Handle interest selection
  const handleInterestSelect = (interest) => {
    // Check if interest is already selected (by name)
    if (selectedInterests.some(i => i.name === interest.name)) {
      // Remove interest
      setSelectedInterests(selectedInterests.filter(i => i.name !== interest.name));
      
      // Remove corresponding video card
      setVideoCards(videoCards.filter(card => card.interest.name !== interest.name));
    } else {
      // Check if we've reached the maximum of 5 interests
      if (selectedInterests.length >= 5) {
        alert('You can select a maximum of 5 interests');
        return;
      }
      
      // Add interest
      setSelectedInterests([...selectedInterests, interest]);
      
      // Add new video card
      setVideoCards([...videoCards, {
        interest: interest,
        prompt: ``,
        generatedVideoUrl: null,
        isGenerating: false,
        error: null,
        taskId: null
      }]);
    }
  };
  
  // Update card prompt
  const updateCardPrompt = (index, newPrompt) => {
    const updatedCards = [...videoCards];
    updatedCards[index].prompt = newPrompt;
    setVideoCards(updatedCards);
  };
  
  // Generate video for a specific card - UPDATED to match your previous implementation
  const generateVideo = async (index) => {
    // Check if user is authenticated
    if (!isUserAuthenticated()) {
      console.log('User not authenticated, cannot generate video');
      return;
    }

    if (!videoCards[index] || !sourceVideo) return;
    
    const card = videoCards[index];
    
    // Check if we have enough credits
    const creditCost = selectedModel === 'runway' ? 40 : 80;
    
    // Update card status
    const updatedCards = [...videoCards];
    updatedCards[index].isGenerating = true;
    updatedCards[index].error = null;
    setVideoCards(updatedCards);
    
    try {
      // Create form data for the API request
      const formData = new FormData();

      // Log the source video details before adding to FormData
      console.log('Source video before FormData:', {
        name: sourceVideo.name,
        type: sourceVideo.type,
        size: sourceVideo.size,
        lastModified: sourceVideo.lastModified
      });

      // Determine if it's a video or image and use appropriate field name
      const isVideo = sourceVideo.type.startsWith('video/');
      if (isVideo) {
        formData.append('sourceVideo', sourceVideo, sourceVideo.name);
      } else {
        formData.append('sourceImage', sourceVideo, sourceVideo.name);
      }
      
      // Add the interest as a JSON array
      const interestName = card.interest.name;
      formData.append('interests', JSON.stringify([interestName]));
      
      // Add the prompt
      formData.append('prompt', card.prompt);
      
      // Add model information
      if (selectedModel === 'runway') {
        formData.append('model', 'runway');
      } else if (selectedModel === 'luma-photon') {
        formData.append('model', 'luma-photon');
      } else if (selectedModel.startsWith('luma-')) {
        formData.append('model', 'luma');
        formData.append('lumaModel', selectedModel.replace('luma-', ''));
      } else {
        formData.append('model', selectedModel);
      }
      
      console.log('Sending request with:', {
        fileSize: sourceVideo.size,
        fileName: sourceVideo.name,
        fileType: sourceVideo.type,
        isVideo: sourceVideo.type.startsWith('video/'),
        interest: interestName,
        prompt: card.prompt,
        model: selectedModel
      });
      
      // Log the complete FormData contents
      console.log('FormData contents before sending:', {
        hasSourceVideo: formData.has('sourceVideo'),
        hasSourceImage: formData.has('sourceImage'),
        interests: formData.get('interests'),
        prompt: formData.get('prompt'),
        model: formData.get('model'),
        lumaModel: formData.get('lumaModel')
      });

      // Log the actual file in FormData
      const fileInFormData = formData.get('sourceVideo') || formData.get('sourceImage');
      console.log('File in FormData:', fileInFormData ? {
        name: fileInFormData.name,
        type: fileInFormData.type,
        size: fileInFormData.size
      } : 'No file found in FormData');
      
      // Use your API utility instead of direct axios calls
      const response = await api.transformVideo(formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 300000 // 5 minutes timeout
      });
      const taskId = response.data.taskId;
      
      // Update the card with the task ID and isFromVideo flag
      updatedCards[index].taskId = taskId;
      updatedCards[index].isFromVideo = isVideo;
      setVideoCards(updatedCards);
      
      // Start polling for task status
      pollTaskStatus(taskId, index);
      
      // Fetch updated credit information from the server
      // fetchCredits();
      
      return true; // Indicate success
    } catch (error) {
      console.error('Error generating video:', error);
      
      // Parse the error message to provide more context
      let errorMessage = 'Error generating video. Please try again.';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.error && errorData.error.includes('Invalid asset aspect ratio')) {
          errorMessage = 'The image has an invalid aspect ratio. Please use an image with width/height ratio between 0.5 and 2.';
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Update card with error
      const updatedCards = [...videoCards];
      updatedCards[index].isGenerating = false;
      updatedCards[index].error = errorMessage;
      setVideoCards(updatedCards);
      
      // Check if all videos are done generating
      const stillGenerating = updatedCards.some(card => card.isGenerating);
      if (!stillGenerating) {
        setAnalyzing(false);
      }
      
      return false; // Indicate failure
    }
  };
  
  // Generate all videos
  const generateAllVideos = async () => {
    // Check if user is authenticated
    if (!isUserAuthenticated()) {
      console.log('User not authenticated, cannot generate videos');
      throw new Error('You must be logged in to generate videos');
    }

    if (!sourceVideo) {
      throw new Error('No source video selected');
    }
    
    // Check if we have any cards to generate
    const cardsToGenerate = videoCards.filter(card => !card.generatedVideoUrl && !card.isGenerating);
    if (cardsToGenerate.length === 0) {
      throw new Error('No videos to generate');
    }
    
    try {
      // Set analyzing to true
      setAnalyzing(true);
      
      // Generate each video, but don't throw if individual ones fail
      const results = await Promise.allSettled(
        cardsToGenerate.map((card, cardIndex) => {
          const index = videoCards.findIndex(c => c.interest.name === card.interest.name);
          return generateVideo(index);
        })
      );
      
      // Check if all videos failed to generate
      const allFailed = results.every(result => result.status === 'rejected');
      if (allFailed) {
        throw new Error('All videos failed to generate');
      }
      
      // Check if any videos are actually generating
      const anyGenerating = videoCards.some(card => card.isGenerating);
      if (!anyGenerating) {
        setAnalyzing(false);
      }
      
      return true;
    } catch (error) {
      console.error('Error generating videos:', error);
      setAnalyzing(false); // Reset analyzing state on error
      throw error; // Re-throw the error so it can be caught by the component
    }
  };
  
  // Poll for task status - UPDATED to match your previous implementation
  const pollTaskStatus = async (taskId, index) => {
    // Check if user is authenticated
    if (!isUserAuthenticated()) {
      console.log('User not authenticated, cannot poll task status');
      return;
    }
    
    // Poll every 5 seconds until task completes
    const interval = setInterval(async () => {
      try {
        // Use your API utility instead of direct axios calls
        const statusResponse = await api.checkTransformVideoStatus(taskId);
        const { status, videoUrl, error, isFromVideo } = statusResponse.data;

        console.log('Task status update:', { 
          taskId, 
          status, 
          videoUrl, 
          isFromVideo 
        });
        
        if (status === 'completed' && videoUrl) {
          // Clear the interval
          clearInterval(interval);
          
          // Update card with video URL
          const updatedCards = [...videoCards];
          updatedCards[index].isGenerating = false;
          updatedCards[index].generatedVideoUrl = videoUrl;
          updatedCards[index].isFromVideo = isFromVideo;

          console.log('Updated card:', updatedCards[index]);

          setVideoCards(updatedCards);
          
          // Play success sound
          playSuccessSound();
          
          // Check if all videos are done generating
          const stillGenerating = updatedCards.some(card => card.isGenerating);
          if (!stillGenerating) {
            setAnalyzing(false);
          }
        } else if (status === 'failed' || error) {
          // Clear the interval
          clearInterval(interval);
          
          // Update card with error
          const updatedCards = [...videoCards];
          updatedCards[index].isGenerating = false;
          updatedCards[index].error = error || 'Video generation failed';
          setVideoCards(updatedCards);
          
          // Check if all videos are done generating
          const stillGenerating = updatedCards.some(card => card.isGenerating);
          if (!stillGenerating) {
            setAnalyzing(false);
          }
        }
        // If still processing, continue polling
      } catch (error) {
        console.error('Error checking status:', error);
        
        // Clear the interval on API errors after a few retries
        clearInterval(interval);
        
        // Update card with error from the API call itself
        const updatedCards = [...videoCards];
        updatedCards[index].isGenerating = false;
        updatedCards[index].error = 'Error checking generation status: ' + (error.message || 'Unknown error');
        setVideoCards(updatedCards);
        
        // Check if all videos are done generating
        const stillGenerating = updatedCards.some(card => card.isGenerating);
        if (!stillGenerating) {
          setAnalyzing(false);
        }
      }
    }, 5000); // Check every 5 seconds
    
    // Store the interval ID so it can be cleared if needed
    if (!window.taskIntervals) {
      window.taskIntervals = {};
    }
    window.taskIntervals[taskId] = interval;
    
    // Clean up function
    return () => {
      if (window.taskIntervals && window.taskIntervals[taskId]) {
        clearInterval(window.taskIntervals[taskId]);
        delete window.taskIntervals[taskId];
      }
    };
  };

  const clearErrors = () => {
    const updatedCards = videoCards.map(card => ({
      ...card,
      error: null
    }));
    setVideoCards(updatedCards);
  };
  
  // Download video
  const downloadVideo = async (url, filename) => {
    try {
      // Fetch the video as a blob
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
      
      return true;
    } catch (error) {
      console.error('Error downloading video:', error);
      return false;
    }
  };
  
  // Set source video
  const setVideo = (video) => {
    setSourceVideo(video);
  };
  
  // Set model
  const setModel = (model) => {
    setSelectedModel(model);
  };
  
  return (
    <VideoGenerationContext.Provider value={{
      selectedInterests,
      videoCards,
      analyzing,
      sourceVideo,
      selectedModel,
      // credits,
      handleInterestSelect,
      updateCardPrompt,
      generateVideo,
      generateAllVideos,
      downloadVideo,
      setVideo,
      setModel,
      // fetchCredits,
      clearErrors,
    }}>
      {children}
    </VideoGenerationContext.Provider>
  );
};

export const useVideoGeneration = () => useContext(VideoGenerationContext);